import React, { useMemo } from 'react';
import { Handle, Position, useStore } from 'reactflow';
import { GitBranch, Clock, AlertTriangle } from 'lucide-react';

/**
 * Normalise button text the same way the backend does
 * (lowercase, trim, collapse whitespace, strip trailing punctuation).
 * Keep this in sync with _normalise_button_text() in journey_module.py.
 */
export const normaliseButtonText = (text) => {
  if (!text) return '';
  let out = String(text).trim().toLowerCase();
  out = out.replace(/\s+/g, ' ');
  out = out.replace(/[!?.,;:]+$/g, '');
  return out;
};

/**
 * Walk upstream from this node to find the most recent action node that sends
 * a WhatsApp template. Read its selected template's QUICK_REPLY buttons.
 */
const useUpstreamButtons = (nodeId) => {
  const nodes = useStore((s) => s.getNodes());
  const edges = useStore((s) => s.edges);
  
  return useMemo(() => {
    // BFS backwards from this node, skipping non-action nodes (delay, etc.)
    const incoming = new Map();
    edges.forEach((e) => {
      if (!incoming.has(e.target)) incoming.set(e.target, []);
      incoming.get(e.target).push(e.source);
    });
    
    const visited = new Set();
    const queue = [...(incoming.get(nodeId) || [])];
    while (queue.length) {
      const curr = queue.shift();
      if (!curr || visited.has(curr)) continue;
      visited.add(curr);
      const node = nodes.find((n) => n.id === curr);
      if (!node) continue;
      
      const data = node.data || {};
      // Looking for a WhatsApp-sending action node
      if (node.type === 'action' && (data.action_type === 'whatsapp' || !data.action_type)) {
        // 1) Preferred source: persisted QR button labels saved when the
        //    template was selected.
        if (Array.isArray(data.template_qr_buttons) && data.template_qr_buttons.length > 0) {
          return { buttons: data.template_qr_buttons, templateName: data.template_name || null };
        }
        const tpl = data.selected_template || data._template_meta;
        if (tpl && Array.isArray(tpl.components)) {
          const buttonsComp = tpl.components.find((c) => c.type === 'BUTTONS');
          const qrButtons = (buttonsComp?.buttons || []).filter(
            (b) => b.type === 'QUICK_REPLY' && b.text
          );
          if (qrButtons.length > 0) {
            return { buttons: qrButtons.map((b) => b.text), templateName: data.template_name || null };
          }
        }
        // WA node found but buttons not yet persisted — return the name so the
        // config panel can look it up from the templates list.
        return { buttons: [], templateName: data.template_name || null };
      }
      
      // Traverse further up
      (incoming.get(curr) || []).forEach((p) => queue.push(p));
    }
    return { buttons: [], templateName: null };
  }, [nodeId, nodes, edges]);
};

const ButtonRouterNode = ({ id, data, selected }) => {
  const timeoutHours = data.timeout_hours || 24;
  const upstream = useUpstreamButtons(id);
  
  // Prefer manually-configured buttons; else auto-detected ones.
  const configuredButtons = Array.isArray(data.buttons) && data.buttons.length > 0
    ? data.buttons
    : upstream.buttons;
  
  const hasButtons = configuredButtons.length > 0;
  
  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 min-w-[220px] shadow-lg ${
        selected
          ? 'border-purple-400 bg-purple-500/20'
          : 'border-purple-500/50 bg-slate-800/90'
      }`}
      data-testid={`button-router-node-${id}`}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-purple-400"
      />
      
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-md bg-purple-500/20">
          <GitBranch className="w-4 h-4 text-purple-400" />
        </div>
        <span className="text-xs font-medium text-purple-400 uppercase tracking-wide">
          Button Router
        </span>
      </div>
      
      <div className="text-sm font-medium text-white truncate">
        {data.label || 'Route by Button Reply'}
      </div>
      
      <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
        <Clock className="w-3 h-3" />
        <span>Timeout: {timeoutHours}h</span>
      </div>
      
      {!hasButtons && (
        <div className="flex items-center gap-1 mt-2 text-[11px] text-amber-400">
          <AlertTriangle className="w-3 h-3" />
          <span>Connect after a WA template with QR buttons</span>
        </div>
      )}
      
      {/* Per-button source handles, plus one "timeout" handle */}
      <div className="mt-3 space-y-1.5 border-t border-slate-700 pt-2">
        {configuredButtons.map((btnText, idx) => {
          const handleId = normaliseButtonText(btnText);
          const leftPct = 12 + (idx * (76 / Math.max(configuredButtons.length - 1, 1)));
          return (
            <div
              key={`btn-${idx}-${handleId}`}
              className="flex items-center justify-between text-xs text-slate-300"
            >
              <span className="truncate max-w-[150px]" title={btnText}>
                {btnText}
              </span>
              <span className="text-[10px] text-purple-400">→</span>
              <Handle
                type="source"
                position={Position.Bottom}
                id={handleId}
                className="w-3 h-3 !bg-purple-400"
                style={{
                  left: `${leftPct}%`,
                  bottom: -6,
                }}
              />
            </div>
          );
        })}
        
        {/* Timeout handle always present */}
        <div className="flex items-center justify-between text-xs text-slate-400 pt-1">
          <span className="italic">Timeout</span>
          <span className="text-[10px] text-red-400">→</span>
          <Handle
            type="source"
            position={Position.Bottom}
            id="timeout"
            className="w-3 h-3 !bg-red-400"
            style={{
              left: '92%',
              bottom: -6,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ButtonRouterNode;
